import { memo } from 'react';

import Conversion from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { formatToReactComponent } from 'bloko/common/trl';

import PeriodInDays, { PeriodVariants } from 'src/components/Price/PeriodInDays';
import PriceCartItemHH, { PriceCartItemHHProps } from 'src/components/Price/PriceCart/PriceCartItemHH';
import translation from 'src/components/translation';
import { PriceCartProductType } from 'src/models/price/priceCart';
import { BrandingProductType } from 'src/models/price/product.types';
import { ServiceCode } from 'src/models/price/productCodes';

import Title from 'src/components/Price/PriceCart/PriceCartProduct/Branding/Title';
import getBrandedVacancyTemplateCount from 'src/components/Price/PriceCart/PriceCartProduct/Branding/getBrandedVacancyTemplateCount';

const TrlKeys = {
    template: {
        one: 'price.priceCartProduct.branding.template.one',
        some: 'price.priceCartProduct.branding.template.some',
        many: 'price.priceCartProduct.branding.template.many',
    },
};

interface BrandingProps {
    product: PriceCartProductType<BrandingProductType>;
}

const Branding: TranslatedComponent<BrandingProps> = ({ trls, product }) => {
    const showRegion = product.code !== ServiceCode.BrandingBuilderVacancy && product.regionTrl;
    const brandedVacancyTemplateCount = getBrandedVacancyTemplateCount(product);
    const hasBrandedVacancyTemplate = !!brandedVacancyTemplateCount;

    const list = [
        hasBrandedVacancyTemplate && {
            key: 'templateCount',
            element: (
                <Conversion
                    one={trls[TrlKeys.template.one]}
                    some={trls[TrlKeys.template.some]}
                    many={trls[TrlKeys.template.many]}
                    hasValue={false}
                    format={(trl) =>
                        formatToReactComponent(trl, {
                            '{0}': <span data-qa="price-cart-branding__count">{brandedVacancyTemplateCount}</span>,
                        })
                    }
                    value={brandedVacancyTemplateCount}
                />
            ),
        },
        {
            key: 'period',
            element: (
                <PeriodInDays
                    period={product.period}
                    variant={hasBrandedVacancyTemplate ? PeriodVariants.Default : PeriodVariants.Option}
                />
            ),
        },
        showRegion && {
            key: 'region',
            element: (
                <p>
                    <span data-qa="cart-item__region">{product.regionTrl}</span>
                </p>
            ),
        },
    ].filter(Boolean) as PriceCartItemHHProps['list'];

    return (
        <PriceCartItemHH
            dataQa={`cart-item__code-${product.code}`}
            product={product}
            list={list}
            title={<Title code={product.code} />}
        />
    );
};

export default memo(translation(Branding));
